import removeObjectFromArray from '@afosto/utils/array/removeObjectFromArray';
import updateObjectInArray from '@afosto/utils/array/updateObjectInArray';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import reorderArray from '@afosto/utils/array/reorderArray';

/**
 * Return a view by key.
 * @param {string} key
 */
const getView = key => ({ fixedViews, views }) =>
  findObjectInArray([...fixedViews, ...views], 'key', key);

/**
 * Create a new view
 * @param {object} view
 * @returns {Promise}
 */
const createView = view => async ({ onAddView, setViews, setActiveView, views }) => {
  try {
    if (onAddView) {
      await onAddView(view, views);
    }

    setViews([...views, view]);
    setActiveView(view);
  } catch (err) {
    // TODO: Do something with the error.
  }
};

/**
 * Sets the active state of the view to false.
 * @param {string} viewKey
 * @returns {Promise}
 */
const deleteView = viewKey => async ({
  onDeleteView,
  setViews,
  views,
  activeView,
  setActiveView,
  defaultView,
}) => {
  try {
    const view = findObjectInArray(views, 'key', viewKey);

    if (!view) {
      return;
    }

    const deletedView = { ...view, active: false, isDefault: false };

    if (onDeleteView) {
      await onDeleteView(deletedView);
    }

    setViews(removeObjectFromArray(views, view, 'key'));

    if (view.key === activeView.key) {
      setActiveView(defaultView);
    }
  } catch (err) {
    // TODO: Do something with the error.
  }
};

/**
 * Update an existing view.
 * @param {object} view
 * @returns {Promise}
 */
const updateView = view => async ({ views, onUpdateView, setViews, activeView, setActiveView }) => {
  try {
    const oldView = findObjectInArray(views, 'key', view.key);

    if (onUpdateView) {
      await onUpdateView(view, oldView);
    }

    setViews(updateObjectInArray(views, view, 'key'));

    if (view.key === activeView.key) {
      setActiveView(view);
    }
  } catch (err) {
    // TODO: Do something with the error.
  }
};

/**
 * Reposition a view within the available views.
 * @param {Number} startPosition
 * @param {Number} endPosition
 * @returns {Promise}
 */
const repositionView = (startPosition, endPosition) => async ({
  views,
  onRepositionView,
  setViews,
}) => {
  try {
    const activeViews = views.filter(view => view.active);
    const deletedViews = views.filter(view => !view.active);
    const repositionedViews = reorderArray([...activeViews, ...deletedViews], {
      start: startPosition,
      end: endPosition,
    });

    if (onRepositionView) {
      await onRepositionView(repositionedViews);
    }

    setViews(repositionedViews);
  } catch (err) {
    // TODO: Do something with the error.
  }
};

/**
 * Return the active view with the default view as fallback.
 * @returns {object|null}
 */
const getActiveView = () => ({ activeView, defaultView }) => activeView || defaultView;

/**
 * Set the active view.
 * @param {string} viewKey
 */
const setActiveView = viewKey => ({
  fixedViews,
  views,
  setActiveView: setActiveViewState,
  defaultView,
}) => {
  const view = findObjectInArray([...fixedViews, ...views], 'key', viewKey);

  if (view && viewKey !== defaultView.key) {
    setActiveViewState(view);
  } else {
    setActiveViewState(null);
  }
};

const setDefaultView = view => async ({
  onChangeDefaultView,
  views,
  activeView: currentActiveView,
  defaultView,
  setViews,
  setActiveView: setActiveViewState,
}) => {
  try {
    const activeView = currentActiveView || defaultView;
    const updatedViews = views.reduce(
      (acc, viewData) => [...acc, { ...viewData, isDefault: viewData?.key === view?.key }],
      [],
    );

    if (activeView?.key !== 'default') {
      setActiveViewState({ ...activeView, isDefault: true });
    }

    if (onChangeDefaultView) {
      await onChangeDefaultView(updatedViews);
    }

    setViews(updatedViews);
  } catch (err) {
    // Do nothing.
  }
};

/**
 * Return the available active views.
 * @returns {function({views: *}): *}
 */
const getAvailableViews = () => ({ views }) => views.filter(view => view.active);

export default {
  getView,
  createView,
  deleteView,
  updateView,
  repositionView,
  getActiveView,
  setActiveView,
  getAvailableViews,
  setDefaultView,
};
